import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, HelpCircle, Radio } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Simplified Tool Page Header v3
 * 
 * ONLY provides page-specific controls:
 * - Back button
 * - Page title
 * - Help button (optional)
 * - Custom right content (optional)
 * 
 * NOTE: Language Switcher, Home, Logout, User info are now in the global TopNavBar.
 * This header should NOT duplicate those controls.
 */
const ToolPageHeader = ({ 
  title, 
  onHelpClick, 
  showHelp = false,
  rightContent = null 
}) => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  return (
    <header className="bg-[#1a1625]/80 backdrop-blur-sm border-b border-purple-500/20">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2 sm:py-3">
        <div className="flex items-center justify-between gap-2">
          
          {/* Left Side - Back + Logo + Title */}
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            {/* Back Button */}
            <Button
              onClick={() => navigate(-1)}
              variant="outline"
              size="sm"
              className="bg-purple-600 hover:bg-purple-700 text-white border-purple-500 flex-shrink-0 h-8 px-2 sm:px-3"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline ml-1">{t('back') || 'Πίσω'}</span>
            </Button>
            
            {/* Logo Icon */}
            <div 
              className="hidden sm:flex bg-gradient-to-r from-[#2D0C4B] to-[#4A1D7A] p-2 rounded-lg cursor-pointer hover:shadow-lg transition-shadow flex-shrink-0"
              onClick={() => navigate('/about')}
            >
              <Radio className="w-4 h-4 text-white" />
            </div>
            
            {/* Title */}
            <div className="min-w-0 flex-1">
              <h1 className="text-sm sm:text-base md:text-lg font-bold text-white truncate">
                <span className="hidden md:inline text-purple-300">Radio Solutions - </span>
                {title}
              </h1>
            </div>
          </div>

          {/* Right Side - Only page-specific controls */}
          <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
            
            {/* Help Button */}
            {showHelp && onHelpClick && (
              <Button
                onClick={onHelpClick}
                variant="outline"
                size="sm"
                className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border-amber-500/50 h-8 px-2 sm:px-3"
                data-testid="help-button"
              >
                <HelpCircle className="w-4 h-4" />
                <span className="hidden sm:inline ml-1">{t('instructions') || 'Οδηγίες'}</span>
              </Button>
            )}
            
            {/* Custom Right Content */}
            {rightContent}
          </div>
        </div>
      </div>
    </header>
  );
};

export default ToolPageHeader;
